import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import SectionHeading from "./SectionHeading";
import { useLanguage } from "../context/LanguageContext";

export default function ResetPasswordPage() {
  const { t } = useLanguage();
  const { resetPassword } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const inputClass =
    "border border-steel-300 px-3 py-2.5 text-sm text-ink outline-none focus:border-ink";
  const labelClass = "text-sm font-semibold text-steel-500";

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError(t("authPasswordMismatch"));
      return;
    }

    setIsSubmitting(true);
    const result = await resetPassword(token, password);
    setIsSubmitting(false);
    if (result.ok) {
      setDone(true);
    } else {
      setError(result.error);
    }
  };

  return (
    <section className="bg-white pb-24 pt-40 md:pb-32">
      <div className="container-industrial">
        <SectionHeading title={t("resetPageTitle")} />

        <div className="mt-12 max-w-sm border border-steel-200 p-8">
          {!token ? (
            <p className="text-sm text-accent">
              {t("resetInvalidLinkPrefix")}{" "}
              <Link to="/login" className="text-accent hover:text-accent-hover">
                {t("resetLoginPageLinkText")}
              </Link>
              .
            </p>
          ) : done ? (
            <div className="flex flex-col gap-3">
              <p className="text-sm leading-relaxed text-steel-600">
                {t("resetSuccessMessage")}
              </p>
              <Link to="/login" className="btn-primary mt-2 w-full justify-center">
                {t("resetToLoginLink")}
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <label className="flex flex-col gap-1.5">
                <span className={labelClass}>{t("resetNewPasswordLabel")}</span>
                <input
                  required
                  autoFocus
                  minLength={6}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputClass}
                />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className={labelClass}>{t("authConfirmPasswordLabel")}</span>
                <input
                  required
                  minLength={6}
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={inputClass}
                />
              </label>
              {error && <p className="text-sm text-accent">{error}</p>}
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary mt-2 w-full disabled:opacity-60"
              >
                {isSubmitting ? "…" : t("resetSubmit")}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
